import {
  numberToCurrencyString,
  renderMultiple,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import { companiesHouseUrl } from 'adminConstants/companyProperties';
import SocialMediaLink from './SocialMediaLink';
import {
  FileCopy,
  Edit,
  Delete,
  CloudDownload,
  Visibility,
} from '@mui/icons-material';
import {
  createCompanyDuplicateRoute,
  createCompanyEditRoute,
} from 'adminConstants/routes';
import TableActions from 'components/Table/TableActions';
import { DownloadShareholdingsAction } from './DownloadShareholdingsAction';

type GenerateMainTableColumnsArgs = {
  isSuperAdmin: boolean;
  classes: any;
};

type GenerateCollapsedTableColumnsArgs = {
  classes: any;
  updatePermission: boolean;
  deletePermission: boolean;
  createPermission: boolean;
  deleteDisclosure: any;
  history: any;
};

export const generateMainTableColumns = ({
  isSuperAdmin,
  classes,
}: GenerateMainTableColumnsArgs) => [
  {
    label: 'Legal name',
    key: 'legalName',
  },
  {
    label: 'Company',
    key: 'tradingName',
    sort: true,
  },
  ...(isSuperAdmin
    ? [
        {
          label: 'Firm',
          render: (elm: any) => elm?.firmName,
          key: 'firmName',
        },
      ]
    : []),
  {
    label: 'Shares held',
    key: 'totalSharesHeldInCompany',
    render: (elm: any) =>
      numberToDisplayString(elm.metrics?.sharesHeldInCompany),
  },
  {
    label: 'Initial value',
    key: 'initialValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.metrics?.initialValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Hold value',
    key: 'currentHoldValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.metrics?.currentHoldValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Gross realised value',
    key: 'grossRealisedValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.metrics?.grossRealisedValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Total value',
    key: 'totalValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.metrics?.totalValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Blended multiple',
    key: 'blendedMultiple',
    sort: false,
    render: (elm: any) => renderMultiple(elm.metrics?.blendedMultiple),
  },
  {
    label: 'Last revaluation',
    key: 'latestRevaluationDate',
    sort: false,
    render: (elm: any) => dateToLabel(elm.metrics?.latestRevaluationDate),
  },
  {
    label: 'Links',
    key: '',
    sort: false,
    render: (elm: any) => (
      <div className={classes.dFlex}>
        <SocialMediaLink
          url={elm?.links?.linkedinUrl}
          iconSrc="images/company/linkedin.png"
          altText="LinkedIn"
        />
        <SocialMediaLink
          url={elm?.links?.twitterUrl}
          iconSrc="images/company/twitter.png"
          altText="Twitter"
        />
        <SocialMediaLink
          url={elm?.links?.url}
          iconSrc="images/company/url.png"
          altText="Website"
        />
        <SocialMediaLink
          url={
            elm?.links?.companiesHouseNumber
              ? companiesHouseUrl + elm.links.companiesHouseNumber
              : ''
          }
          iconSrc="images/company/companies-house-number.png"
          altText="Companies House"
        />
      </div>
    ),
  },
];

export const generateCollapsedTableColumns = ({
  updatePermission,
  deletePermission,
  createPermission,
  deleteDisclosure,
}: GenerateCollapsedTableColumnsArgs) => [
  {
    label: 'Company',
    key: 'tradingName',
    sort: true,
  },
  {
    label: 'Share class',
    key: 'shareClass',
  },
  {
    label: 'Shares held',
    key: 'sharesHeldInCompany',
    render: (elm: any) => numberToDisplayString(elm.sharesHeldInCompany),
  },
  {
    label: 'Initial value',
    key: 'initialValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.initialValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Hold value',
    key: 'currentHoldValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.currentHoldValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Gross realised value',
    key: 'grossRealisedValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.grossRealisedValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Total value',
    key: 'totalValue',
    sort: false,
    render: (elm: any) =>
      numberToCurrencyString(elm.totalValue, {
        unlimitedDp: false,
      }),
  },
  {
    label: 'Blended multiple',
    key: 'blendedMultiple',
    sort: false,
    render: (elm: any) => renderMultiple(elm.blendedMultiple),
  },
  {
    label: 'Last revaluation',
    key: 'latestRevaluationDate',
    sort: false,
    render: (elm: any) => dateToLabel(elm.latestRevaluationDate),
  },
  {
    label: 'Actions',
    key: 'actions',
    sort: false,
    render: (elm: any) => (
      <TableActions
        showAsDropdown
        actions={[
          {
            label: updatePermission ? 'Edit' : 'View',
            icon: updatePermission ? Edit : Visibility,
            color: 'primary',
            link: createCompanyEditRoute(elm._id),
          },
          {
            label: 'Duplicate',
            icon: FileCopy,
            color: 'primary',
            visible: createPermission,
            link: createCompanyDuplicateRoute(elm._id),
          },
          {
            label: 'Download shareholdings',
            icon: CloudDownload,
            color: 'primary',
            actionComponent: ({ onClick, disabled }) => (
              <DownloadShareholdingsAction
                companyId={elm._id}
                tradingName={elm.tradingName}
                onClick={onClick}
                disabled={disabled}
              />
            ),
          },
          {
            label: 'Delete',
            icon: Delete,
            color: 'error',
            visible: deletePermission,
            onClick: () => deleteDisclosure.stageAction(elm),
          },
        ]}
      />
    ),
  },
];
